/**
 * Service-layer wrappers around the backend API. Every backend call goes
 * through here — screens and hooks import from this module, never from
 * `client.ts` directly.
 *
 * Wire shapes are camelCase; we adapt them to the in-memory legacy shapes
 * (e.g. `colors`, `time`, `home`/`away`) so screens don't have to be
 * rewritten in lockstep with the backend cutover. Adapter layer is small
 * and obvious — when the screens are next refactored, this file collapses.
 */

import { api } from '@/api/client';
import type {
  Country,
  CountryCode,
  Event,
  EventKind,
  Match,
  MatchStatus,
  MeProfile,
  NotificationPrefs,
  Plan,
  Venue,
} from '@/types/domain';
import { formatKickoffTime } from '@/utils/time';

// ── Wire shapes (matching backend Pydantic schemas) ──────────────────────

interface WireVenue {
  id: string;
  countryCode: CountryCode;
  name: string;
  type: string;
  hood: string;
  photoSlug: string;
  lat: number;
  lng: number;
  googleMapsUrl: string | null;
  goingCount: number;
  /** Optional — backend doesn't send this yet. Maps straight through when it does. */
  imageUrl?: string | null;
  /** Venue's Instagram or Facebook link. Nullable. */
  socialsUrl?: string | null;
}

interface WireCountry {
  code: CountryCode;
  name: string;
  flagEmoji: string;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  tagline: string;
  displayOrder: number;
  neighborhoods: string[];
  curated: boolean;
}

interface WireMatch {
  id: string;
  homeCode: CountryCode | null;
  awayCode: CountryCode | null;
  homeLabel: string | null;
  awayLabel: string | null;
  kickoffAt: string;
  stage: string;
  stadium: string;
  status?: MatchStatus | null;
  isLive: boolean;
  minute?: number | null;
  score?: string | null;
}

interface WireMeProfile {
  id: string;
  displayName: string | null;
  username: string | null;
  follows: CountryCode[];
  notificationPreferences: NotificationPrefs;
}

interface WireEvent {
  id: string;
  kind: EventKind;
  title: string;
  startsAt: string;
  endsAt: string | null;
  countryCode: CountryCode | null;
  matchId: string | null;
  venueId: string | null;
  venueName: string | null;
  venueHood: string | null;
  imageUrl: string | null;
  sourceUrl: string | null;
  venueMapUrl: string | null;
  isFree: boolean;
  goingCount: number;
  isHot: boolean;
}

// ── Adapters ─────────────────────────────────────────────────────────────

function venueFromWire(w: WireVenue): Venue {
  return {
    id: w.id,
    countryCode: w.countryCode,
    name: w.name,
    type: w.type,
    hood: w.hood,
    distance: '', // backend dropped this; UI hides the line where it appears
    goingCount: w.goingCount,
    friends: 0, // friends feature dropped from product scope
    photo: w.photoSlug,
    lat: w.lat,
    lng: w.lng,
    googleMapsUrl: w.googleMapsUrl ?? null,
    imageUrl: w.imageUrl ?? null,
    socialsUrl: w.socialsUrl ?? null,
  };
}

function countryFromWire(w: WireCountry): Country {
  return {
    code: w.code,
    name: w.name,
    flag: w.flagEmoji,
    colors: {
      primary: w.primaryColor,
      secondary: w.secondaryColor,
      tertiary: w.tertiaryColor,
    },
    tagline: w.tagline,
    neighborhoods: w.neighborhoods,
    curated: w.curated,
  };
}

function matchFromWire(w: WireMatch): Match {
  // Prefer the backend's explicit status; fall back to deriving it from the
  // legacy flags for any payload (or mock) that predates the status field.
  const status: MatchStatus =
    w.status ?? (w.isLive ? 'LIVE' : w.score ? 'FINISHED' : 'SCHEDULED');
  return {
    id: w.id,
    home: w.homeCode,
    away: w.awayCode,
    homeLabel: w.homeLabel,
    awayLabel: w.awayLabel,
    kickoffAt: w.kickoffAt,
    time: formatKickoffTime(w.kickoffAt),
    stage: w.stage,
    stadium: w.stadium,
    status,
    live: status === 'LIVE',
    minute: w.minute ?? null,
    score: w.score ?? null,
  };
}

function meFromWire(w: WireMeProfile): MeProfile {
  return {
    id: w.id,
    name: w.displayName,
    username: w.username,
    follows: w.follows,
    notificationPreferences: w.notificationPreferences,
  };
}

function eventFromWire(w: WireEvent): Event {
  return {
    id: w.id,
    kind: w.kind,
    title: w.title,
    startsAt: w.startsAt,
    endsAt: w.endsAt,
    countryCode: w.countryCode,
    matchId: w.matchId,
    venueId: w.venueId,
    venueName: w.venueName,
    venueHood: w.venueHood,
    imageUrl: w.imageUrl,
    sourceUrl: w.sourceUrl,
    venueMapUrl: w.venueMapUrl,
    isFree: w.isFree,
    goingCount: w.goingCount,
    isHot: w.isHot,
  };
}

// `exactOptionalPropertyTypes` makes `{ signal: undefined }` fail the type
// check; build options conditionally.
function opts(signal?: AbortSignal): { signal?: AbortSignal } {
  return signal ? { signal } : {};
}

// ── Public API ───────────────────────────────────────────────────────────

export async function listCountries(signal?: AbortSignal): Promise<Country[]> {
  const wire = await api.get<WireCountry[]>('/v1/countries', opts(signal));
  return wire.map(countryFromWire);
}

export async function getCountry(code: CountryCode, signal?: AbortSignal): Promise<Country> {
  const wire = await api.get<WireCountry>(`/v1/countries/${code}`, opts(signal));
  return countryFromWire(wire);
}

interface WireMeta {
  venuesVersion: string;
}

export interface AppMeta {
  /** Cache-busting token for the venues dataset; stamp onto venue requests. */
  venuesVersion: string;
}

/** Uncached content-version signal (see docs/spikes/venues-stale-cache-fix.md). */
export async function getMeta(signal?: AbortSignal): Promise<AppMeta> {
  const wire = await api.get<WireMeta>('/v1/meta', opts(signal));
  return { venuesVersion: wire.venuesVersion };
}

export async function listVenues(
  country?: CountryCode,
  signal?: AbortSignal,
  version?: string,
): Promise<Venue[]> {
  const qs = new URLSearchParams();
  if (country) qs.set('country', country);
  // `v` doesn't affect the query result — it's a cache key, so the 1-hour
  // venue cache invalidates the moment the dataset version changes.
  if (version) qs.set('v', version);
  const query = qs.toString();
  const path = query ? `/v1/venues?${query}` : '/v1/venues';
  const wire = await api.get<WireVenue[]>(path, opts(signal));
  return wire.map(venueFromWire);
}

export async function listMatches(signal?: AbortSignal): Promise<Match[]> {
  const wire = await api.get<WireMatch[]>('/v1/matches', opts(signal));
  return wire.map(matchFromWire);
}

export async function getMatch(id: string, signal?: AbortSignal): Promise<Match> {
  const wire = await api.get<WireMatch>(`/v1/matches/${id}`, opts(signal));
  return matchFromWire(wire);
}

export async function getMe(signal?: AbortSignal): Promise<MeProfile> {
  const wire = await api.get<WireMeProfile>('/v1/me', opts(signal));
  return meFromWire(wire);
}

export function updateNotificationPreferences(
  prefs: NotificationPrefs,
  signal?: AbortSignal,
): Promise<NotificationPrefs> {
  return api.put<NotificationPrefs>('/v1/me/notification-preferences', prefs, opts(signal));
}

// ── Web Push ──────────────────────────────────────────────────────────────

export interface VapidKeyResponse {
  publicKey: string;
}

export interface WebPushSubscriptionBody {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

export interface SubscribeResult {
  id: string;
  notificationPreferences: NotificationPrefs;
}

/** The application server key the browser needs before it can subscribe.
 * 503s when web push isn't configured server-side — callers should treat that
 * as "web push unavailable" and hide the UI. */
export function getVapidKey(signal?: AbortSignal): Promise<VapidKeyResponse> {
  return api.get<VapidKeyResponse>('/v1/push/vapid-key', opts(signal));
}

export function savePushSubscription(
  body: WebPushSubscriptionBody,
  signal?: AbortSignal,
): Promise<SubscribeResult> {
  return api.post<SubscribeResult>('/v1/me/push-subscription', body, opts(signal));
}

export function deletePushSubscription(endpoint: string, signal?: AbortSignal): Promise<void> {
  return api.delete<void>('/v1/me/push-subscription', { ...opts(signal), query: { endpoint } });
}

/** Append a point to the user's location trail (proximity notifications +
 * recommendations). Best-effort — callers fire-and-forget. */
export function recordLocation(lat: number, lng: number, signal?: AbortSignal): Promise<void> {
  return api.post<void>('/v1/me/locations', { lat, lng }, opts(signal));
}

export function listFollows(signal?: AbortSignal): Promise<CountryCode[]> {
  return api.get<CountryCode[]>('/v1/me/follows', opts(signal));
}

export function follow(code: CountryCode, signal?: AbortSignal): Promise<void> {
  // Response body is the created Follow row; we don't need it for UI.
  return api.post<void>('/v1/me/follows', { countryCode: code }, opts(signal));
}

export function unfollow(code: CountryCode, signal?: AbortSignal): Promise<void> {
  return api.delete<void>(`/v1/me/follows/${code}`, opts(signal));
}

export function listPlans(signal?: AbortSignal): Promise<Plan[]> {
  return api.get<Plan[]>('/v1/me/plans', opts(signal));
}

/**
 * Upsert a plan for a match. Backend keys plans by (user, match) so a second
 * call with a different `venueId` switches the plan in place — there is no
 * separate "switch venue" endpoint.
 */
export function setPlan(
  matchId: string,
  venueId: string,
  signal?: AbortSignal,
): Promise<Plan> {
  return api.put<Plan>(`/v1/me/plans/${matchId}`, { venueId }, opts(signal));
}

export function cancelPlan(matchId: string, signal?: AbortSignal): Promise<void> {
  return api.delete<void>(`/v1/me/plans/${matchId}`, opts(signal));
}

// ── Bookmarks (saved venues, no match attached) ──────────────────────────

export async function listBookmarks(signal?: AbortSignal): Promise<Venue[]> {
  const wire = await api.get<WireVenue[]>('/v1/me/bookmarks', opts(signal));
  return wire.map(venueFromWire);
}

export function saveVenue(venueId: string, signal?: AbortSignal): Promise<void> {
  return api.put<void>(`/v1/me/bookmarks/${venueId}`, undefined, opts(signal));
}

export function unsaveVenue(venueId: string, signal?: AbortSignal): Promise<void> {
  return api.delete<void>(`/v1/me/bookmarks/${venueId}`, opts(signal));
}

// ── Events ───────────────────────────────────────────────────────────────

export interface ListEventsParams {
  from?: Date | string;
  to?: Date | string;
  kind?: EventKind;
  country?: CountryCode;
}

export async function listEvents(
  params: ListEventsParams = {},
  signal?: AbortSignal,
): Promise<Event[]> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from instanceof Date ? params.from.toISOString() : params.from);
  if (params.to)   qs.set('to',   params.to   instanceof Date ? params.to.toISOString()   : params.to);
  if (params.kind)    qs.set('kind', params.kind);
  if (params.country) qs.set('country', params.country);
  const path = qs.toString() ? `/v1/events?${qs.toString()}` : '/v1/events';
  const wire = await api.get<WireEvent[]>(path, opts(signal));
  return wire.map(eventFromWire);
}

/** Single approved event by id. Used to resolve a shared deep link (`/e/<id>`)
 * when the event has aged out of the default feed window, so the detail sheet
 * still opens. Throws (like other api.get calls) on 404 / network error. */
export async function getEvent(id: string, signal?: AbortSignal): Promise<Event> {
  const wire = await api.get<WireEvent>(`/v1/events/${encodeURIComponent(id)}`, opts(signal));
  return eventFromWire(wire);
}

export function listEventRsvps(signal?: AbortSignal): Promise<string[]> {
  return api.get<string[]>('/v1/me/event-rsvps', opts(signal));
}

export function setEventRsvp(eventId: string, signal?: AbortSignal): Promise<void> {
  // Response body is the created EventRsvp row; we don't need it for UI.
  return api.put<void>(`/v1/me/event-rsvps/${eventId}`, undefined, opts(signal));
}

export function cancelEventRsvp(eventId: string, signal?: AbortSignal): Promise<void> {
  return api.delete<void>(`/v1/me/event-rsvps/${eventId}`, opts(signal));
}
